import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { useVideoPlatform } from "../hooks/useVideoPlatform";
import { toast } from "../toasts";
import { VisitorPresence } from "../types";
import { isScreenSharingAtom, roomAtom, VideoPlatform } from "../video-sdk";
import { CLIENT_QUEUE_NEW_VISITOR_TOAST_TAG } from "./client-information/constants";
import { ClientQueueToastWrapper as ClientQueueToast } from "./ClientQueueToast";
import { useAdmitClientCallback } from "./clients-drawer/useAdmitClientCallback";
import {
  drawerStateAtom,
  newSessionConfirmationDataAtom,
  ProviderDrawerState,
} from "./state";

const IN_SESSION_TOAST_DURATION = 30_000;

/**
 * Returns a unique key for the toast notification for a visitor. The key
 * represents a unique combination of the patient and visitor. This way,
 * multiple visitors joining a session for the same appointment, aka the same
 * patient short ID, will still all have unique keys.
 * @param patientShortId Short ID of the patient
 * @param visitorUuid Grow UUID of the visitor
 * @returns A unique key for the toast notification for this visitor
 */
export function getToastKeyForPatient(
  patientShortId: string,
  visitorUuid: string,
) {
  return `client-queue-toast-${patientShortId}:${visitorUuid}`;
}

export function useShowNewVisitorToastCallback() {
  const getIsScreenSharing = useAtomCallback(
    useCallback((get) => get(isScreenSharingAtom), []),
  );
  const getRoom = useAtomCallback(useCallback((get) => get(roomAtom), []));
  const getDrawerState = useAtomCallback(
    useCallback((get) => get(drawerStateAtom), []),
  );
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setNewSessionConfirmationData = useSetAtom(
    newSessionConfirmationDataAtom,
  );
  const [admitClient] = useAdmitClientCallback();
  const [, getProviderVideoPlatform] = useVideoPlatform();

  /**
   * Callback that shows a toast notification for a new visitor or visitors in
   * the waiting room.
   * @param shortId The short ID of the patient whose appointment the visitor(s)
   * are here for.
   * @param name The name that the visitor entered when joining the waiting
   * room, or else "N people" if there are multiple visitors.
   * @param visitorUuid Grow UUID of the visitor. Undefined if there are
   * multiple visitors.
   */
  function showVisitorToastCallback(
    shortId: VisitorPresence["patientShortId"],
    name: string,
    visitorUuid?: string,
    visitorVideoPlatform?: VideoPlatform | null,
  ) {
    const isInSession = !!getRoom();
    const toastDuration = isInSession ? IN_SESSION_TOAST_DURATION : undefined;
    const isSharingScreen = getIsScreenSharing();
    const clientDrawerIsOpen = getDrawerState() === ProviderDrawerState.CLIENTS;
    const onAdmitHandler = async () => {
      if (!visitorUuid) return;
      await admitClient(visitorUuid, name, shortId);
    };
    if (clientDrawerIsOpen || isSharingScreen) return;

    const onShowStartNewSessionConfirmation = () => {
      if (!visitorUuid) return;

      setNewSessionConfirmationData({
        visitorName: name,
        visitorUuid,
        patientShortId: shortId,
      });
    };

    const onOpenQueue = () => {
      setDrawerState(ProviderDrawerState.CLIENTS);
      // Close all toasts related to new visitors.
      toast.closeByTag(CLIENT_QUEUE_NEW_VISITOR_TOAST_TAG);
    };

    toast.enqueue(
      (ariaProps) => (
        <ClientQueueToast
          participantName={name}
          patientShortId={shortId}
          onAdmit={onAdmitHandler}
          onOpenQueue={onOpenQueue}
          onShowStartNewSessionConfirmation={onShowStartNewSessionConfirmation}
          visitorUuid={visitorUuid}
          visitorVideoPlatform={visitorVideoPlatform}
          providerVideoPlatform={getProviderVideoPlatform()}
          {...ariaProps}
        />
      ),
      {
        timeout: toastDuration,
        purpose: "realtime",
        tags: new Set([CLIENT_QUEUE_NEW_VISITOR_TOAST_TAG]),
        // Visitor UUID is undefined if there are multiple visitors. In that
        // case, the only option for the toast is to open the client queue and
        // admit them individually, and opening the queue also closes the toast.
        key: getToastKeyForPatient(shortId, visitorUuid || ""),
      },
    );
  }
  return showVisitorToastCallback;
}
